import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vacancyService } from '../../services/vacancyService';
import { employeeService } from '../../services/employeeService';
import { authService } from '../../services/authService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import Loader from '../../components/Loader';
import api from '../../config/api';
import EmployeeNav from '../../components/EmployeeNav';
const ApplyVacancy = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(authService.getCurrentUser());
    const [vacancy, setVacancy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [unreadCount, setUnreadCount] = useState(0);
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm();

    const cvFile = watch('cv');

    useEffect(() => {
        loadVacancy();
        checkIfSaved();
    }, [id]);

    // Listen for user data updates (e.g., when CV is uploaded)
    useEffect(() => {
        const handleUserDataUpdate = () => {
            const updatedUser = authService.getCurrentUser();
            setUser(updatedUser);
        };

        window.addEventListener('userDataUpdated', handleUserDataUpdate);
        return () => {
            window.removeEventListener('userDataUpdated', handleUserDataUpdate);
        };
    }, []);

    useEffect(() => {
        if (cvFile && cvFile[0]) {
            setSelectedFile(cvFile[0]);
            setFileName(cvFile[0].name);
        }
    }, [cvFile]);

    useEffect(() => {
        loadUnreadCount();
    }, []);

    const loadUnreadCount = async () => {
        const response = await api.get('/notifications/unread-count');
        setUnreadCount(response.data.count);
    };
    const loadVacancy = async () => {
        try {
            setLoading(true);
            const response = await vacancyService.getById(id);
            if (response.success) {
                setVacancy(response.data);
                if (response.data.status !== 'open') {
                    toast.error('هذه الوظيفة غير متاحة للتقديم');
                    navigate(`/employee/vacancies/${id}`);
                }
            }
        } catch (error) {
            toast.error('فشل تحميل الوظيفة');
            navigate('/employee/vacancies');
        } finally {
            setLoading(false);
        }
    };

    const checkIfSaved = async () => {
        try {
            const response = await vacancyService.getSaved();
            if (response.success) {
                const savedIds = response.data.map(v => v.id);
                setIsSaved(savedIds.includes(parseInt(id)));
            }
        } catch (error) {
            console.error('Failed to check saved status');
        }
    };

    const handleSave = async () => {
        try {
            await vacancyService.save(id);
            setIsSaved(true);
            toast.success('تم حفظ الوظيفة');
        } catch (error) {
            toast.error('فشل حفظ الوظيفة');
        }
    };

    const handleUnsave = async () => {
        try {
            await vacancyService.unsave(id);
            setIsSaved(false);
            toast.success('تم إلغاء الحفظ');
        } catch (error) {
            toast.error('فشل إلغاء الحفظ');
        }
    };

    const onSubmit = async (data) => {
        try {
            setSubmitting(true);
            const formData = new FormData();

            if (data.cv && data.cv[0]) {
                formData.append('cv', data.cv[0]);
            }
            if (data.notes) {
                formData.append('notes', data.notes);
            }

            const response = await vacancyService.apply(id, formData);
            if (response.success) {
                toast.success('تم تقديم الطلب بنجاح!');
                navigate('/employee/applications');
            }
        } catch (error) {
            const message = error.response?.data?.message || 'فشل تقديم الطلب';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const formatLocation = (location) => {
        if (!location) return 'القاهرة';
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            return 'القاهرة';
        }
        return 'القاهرة';
    };

    const formatTimeAgo = (dateString) => {
        if (!dateString) return 'غير محدد';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'اليوم';
        if (diffDays === 1) return 'منذ يوم';
        if (diffDays < 7) return `منذ ${diffDays} أيام`;
        if (diffDays < 30) return `منذ ${Math.floor(diffDays / 7)} أسابيع`;
        if (diffDays < 365) return `منذ ${Math.floor(diffDays / 30)} أشهر`;
        return `منذ ${Math.floor(diffDays / 365)} سنوات`;
    };

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!vacancy) {
        return <div className="text-center py-4">الوظيفة غير موجودة</div>;
    }

    const targetCompany = vacancy.target_company || vacancy.company_details?.target_company || vacancy.company;
    const companyName = vacancy.target_company_name || targetCompany?.name || 'اسم الشركة هنا';
    const companyLogo = targetCompany?.logo || targetCompany?.image;
    const handleBack = () => {
        navigate(-1);
    };
    return (
        <div className="apply-vacancy-page" dir="rtl">
            {/* Header with Navigation */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir='ltr'>
                <div className="notification-icon">
                    <button
                        onClick={() => navigate('/employee/notifications')}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '4px',
                            cursor: 'pointer',
                            position: 'relative'
                        }}
                    >
                        <span className="bell-icon">🔔</span>
                        {unreadCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '-2px',
                                    right: '-2px',
                                    background: '#dc2626',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: '18px',
                                    height: '18px',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                }}
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
                <div className="settings-title">
                    {vacancy.job_title?.name}
                </div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Job Header Section */}
            <div className="job-header-section px-3 mb-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                    <button
                        className="bookmark-btn-detail"
                        onClick={isSaved ? handleUnsave : handleSave}
                    >
                        {isSaved ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                            </svg>
                        )}
                    </button>
                    <div className="flex-grow-1">
                        <h1 className="job-title-detail mb-2">
                            {vacancy.job_title?.name || 'اسم الوظيفه هنا'}
                        </h1>
                    </div>
                    <div className="company-logo-detail">
                        {companyLogo ? (
                            <img
                                src={companyLogo.startsWith('http') ? companyLogo : `${STORAGE_BASE_URL}/${companyLogo}`}
                                alt={companyName}
                                className="logo-img-detail"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div className="company-logo-text-detail" style={{ display: companyLogo ? 'none' : 'flex' }}>
                            <b>{companyName.charAt(0).toUpperCase()}</b>
                        </div>
                    </div>
                </div>

                {/* Tags */}
                <div className="job-tags-detail d-flex gap-2">
                    <span className="job-tag-detail">{companyName}</span>
                    <span className="job-tag-detail">{formatLocation(vacancy.location)} - مصر</span>
                    <span className="job-tag-detail">{formatTimeAgo(vacancy.created_at)}</span>
                </div>
            </div>

            {/* Application Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="apply-form px-3 mb-5">
                {/* CV Upload Section */}
                <div className="form-section-apply mb-4">
                    <h3 className="form-section-title mb-3">السيرة الذاتية</h3>
                    {/* notfitiy user if he not submited cv his current cv will be uploied */}
                    {user?.employee?.cv_file ? (
                        <p className="form-section-description mb-3">
                            يمكنك ترك هذه الخانة فارغة للتقديم بسيرتك الذاتية الحالية
                        </p>
                    ) : (
                        <p className="form-section-description mb-3">
                            <span className="text-danger">يجب عليك تحميل سيرتك الذاتية</span>
                        </p>
                    )}
                    <div className="upload-box">
                        <input
                            type="file"
                            id="cv"
                            accept=".pdf,.doc,.docx"
                            className="file-input-hidden"
                            {...register('cv', {
                                validate: (files) => {
                                    if (files && files[0]) {
                                        const file = files[0];
                                        const maxSize = 10 * 1024 * 1024; // 10MB
                                        if (file.size > maxSize) {
                                            return 'حجم الملف يجب ألا يتجاوز 10 ميجابايت';
                                        }
                                        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
                                        if (!allowedTypes.includes(file.type)) {
                                            return 'يجب أن يكون الملف بصيغة PDF أو DOC أو DOCX';
                                        }
                                    }
                                    return true;
                                },
                            })}
                        />
                        <label htmlFor="cv" className="upload-label">
                            <div className="upload-content">
                                {!fileName ? (
                                    <>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="17 8 12 3 7 8" />
                                            <line x1="12" y1="3" x2="12" y2="15" />
                                        </svg>
                                        <span className="upload-text">تحميل السيرة الذاتية</span>
                                    </>
                                ) : (
                                    <div className="upload-file-info">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                        <span className="upload-text">{fileName}</span>
                                    </div>
                                )}
                            </div>
                        </label>
                        {errors.cv && <span className="error-message-rtl">{errors.cv.message}</span>}
                    </div>
                </div>

                {/* Notes Section */}
                <div className="form-section-apply mb-4">
                    <h3 className="form-section-title mb-3">بعض البيانات عنك</h3>
                    <div className="textarea-box">
                        <textarea
                            id="notes"
                            className="notes-textarea"
                            rows="6"
                            placeholder="اشرح لماذا أنت الشخص المناسب لهذه الوظيفة"
                            {...register('notes', {
                                maxLength: {
                                    value: 2000,
                                    message: 'يجب ألا تتجاوز الملاحظات 2000 حرف',
                                },
                            })}
                        />
                        {errors.notes && <span className="error-message-rtl">{errors.notes.message}</span>}
                    </div>
                </div>
            </form>

            {/* Apply Now Button */}
            <div className="apply-button-container">
                <button
                    className="btn-apply-now"
                    onClick={handleSubmit(onSubmit)}
                    disabled={submitting}
                >
                    {submitting ? 'جاري التقديم...' : 'قدم الآن'}
                </button>
            </div>

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default ApplyVacancy;
